<template lang="pug">
.bq-service-layout(v-if="currentService")
    aside.bq-sidebar
        SideNav

    main.bq-main
        //- banners & warnings (shared across all service pages)
        EmailVerifyBanner

        //- Plan state banners, built in code/plan-copy.ts: one banner per project
        //- state (suspended, disabled, contained, deleted, payment failed, cancel
        //- scheduled), then the over or near limit warning and the bandwidth
        //- banner. Limit rows name what is over and where to delete it.
        template(v-for="b in banners" :key="b.id")
            .bq-banner(:class="'bq-banner--' + b.tone")
                span.material-symbols-outlined.bq-banner-icon(translate="no") {{ b.icon }}
                span.bq-banner-text
                    | {{ b.text }}
                    template(v-for="r in b.rows" :key="r.key")
                        br
                        span(translate="no") {{ r.text }}
                        template(v-if="r.action")
                            |
                            router-link.bq-link(v-if="r.action.to" :to="r.action.to") [{{ r.action.label }}]
                            a.bq-link(v-else :href="r.action.href" target="_blank" rel="noopener") [{{ r.action.label }}]
                        template(v-else-if="r.hint")
                            |  {{ r.hint }}
                    template(v-if="b.after")
                        br
                        | {{ b.after }}
                template(v-for="a in b.actions" :key="a.label")
                    |
                    router-link.bq-link(v-if="a.to" :to="a.to") [{{ a.label }}]
                    a.bq-link(v-else-if="a.href" :href="a.href" target="_blank" rel="noopener") [{{ a.label }}]
                    //- An action that WAITS on something (the project re-fetch behind
                    //- "Check again", the billing portal url) says so while it waits: the
                    //- label grows the inline dot trail and the link stops taking clicks.
                    //- A banner that sat there unchanged after the click read as broken.
                    a.bq-link(
                        v-else
                        :class="{ 'is-busy': isActionBusy(b, a) }"
                        :aria-busy="isActionBusy(b, a) ? 'true' : undefined"
                        @click="runAction(b, a)"
                    )
                        //- Busy: the dots lead the label ("[...Label]") so the text stays
                        //- anchored at the banner's right edge while they tick.
                        | [
                        span.bq-loader.bq-loader--right(v-if="isActionBusy(b, a)" aria-hidden="true")
                        | {{ a.label }}]

        router-view

//- After a paid checkout for a new project (/my-projects/await-<id>): the billing
//- ticket creates the project once the payment is confirmed, so wait for it here.
.bq-await(v-else-if="awaiting")
    BunnyLoader(v-if="awaiting !== 'gave-up'" :overlay="false" label="Setting up your project")
    h1.bq-await-title {{ awaiting === 'gave-up' ? 'Your project is not ready yet' : 'Payment received' }}
    p.bq-await-desc(v-if="awaiting === 'waiting'") Your project is being created. This page opens it as soon as it is ready.
    p.bq-await-desc(v-else-if="awaiting === 'slow'") This is taking longer than usual. You can leave this page: the project appears in your project list once it is ready.
    p.bq-await-desc(v-else) The payment went through, but the project has not appeared yet. It is added to your project list once it is ready. If it does not appear, contact support@broadwayinc.com.
    router-link.bq-cta-link(v-if="awaiting !== 'waiting'" to="/my-projects") Go to your projects

</template>

<script setup lang="ts">
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, onUnmounted, reactive, ref, watch } from "vue";
import { user, customer, openBillingPortal } from "@/code/user";
import { skapi } from "@/main";
import { fullScreenLoader, removeFullScreenLoader } from "@/components/jsComponents";
import {
    currentService,
} from "@/views/project-list";
import { projectBanners } from "@/code/plan-copy";
import { loadPlanSpecs } from "@/code/service-spec";
import type { BannerAction } from "@/code/plan-copy";
import {
    serviceIdList,
    serviceList,
    pullServices,
    recordsRefreshTrigger,
    prefetchAiApiKey,
    prefetchServiceRecords,
    prefetchServiceUsers,
    prefetchDbDirInfo,
} from "@/views/project-list";
import { bgTaskQueue, agentViewMounted, POLL_INTERVAL, MAX_CONCURRENT_BG_POLLS, chatSession } from "@/code/ai_agent";

import SideNav from "@/components/sideNav.vue";
import Modal from "@/components/modal.vue";
import BunnyLoader from "@/components/bunnyLoader.vue";
import EmailVerifyBanner from "@/components/emailVerifyBanner.vue";
import { showToast } from "@/components/toast";
import { completeNewProject, newProjectPath, CHECKOUT_DESCRIPTION_MAX } from "@/views/new-project";

const router = useRouter();
const route = useRoute();

let serviceId = route.path.split("/")[2];

/**
 * The plan state banners, built in code/plan-copy.ts from `service.active`, the
 * suspension attributes on the row and the Stripe subscription. Driven by the
 * state itself, never by the derived `will_terminate` date, so a suspended
 * project with no date still gets its banner.
 */
const banners = computed(() => (currentService.value ? projectBanners(currentService.value) : []));

// Which banner actions are waiting right now, keyed by banner and label. The
// banners are rebuilt from the service on every change, so the key is the pair of
// ids, not the action object, which does not survive a rebuild.
const busyActions = reactive<Record<string, true>>({});
const actionKey = (b: { id: string }, a: BannerAction) => b.id + "\0" + a.label;
const isActionBusy = (b: { id: string }, a: BannerAction) => !!busyActions[actionKey(b, a)];
const runAction = async (b: { id: string }, a: BannerAction) => {
    const key = actionKey(b, a);
    if (busyActions[key]) return;
    busyActions[key] = true;
    try {
        if (a.run === "billing") {
            await openBillingPage();
        } else if (a.run === "refresh") {
            try {
                await currentService.value?.refresh();
            } catch (err) {
                console.warn("[main] could not refresh the project", err);
            }
            currentService.value?.applySubscriptionDerived();
        }
    } finally {
        delete busyActions[key];
    }
};

// Fallback completion-poller for background indexing tasks. agent.vue owns
// polling + chat rendering of bgTaskQueue while it's open; when it's closed
// (user on the db-files or records tab) nothing else advances the queue, so
// indexing completion — and the records.vue auto-refresh it triggers — would
// never fire. While agentViewMounted is false this polls pending tasks, removes
// them when done, and bumps recordsRefreshTrigger when a service's queue drains.
// (`poll` is safe to call repeatedly; an all-cancelled batch just causes a
// harmless extra refresh.)
const bgFallbackPolls = new Set<string>();
const finishBgFallbackTask = (id: string, svc: string) => {
    bgFallbackPolls.delete(id);
    // Release the engine's claim too (see the registration below), so a later
    // drain can attach its own poll for this id if one is ever needed again.
    chatSession.historyItemPolls.delete(id);
    const i = bgTaskQueue.findIndex((e) => e.id === id);
    if (i !== -1) bgTaskQueue.splice(i, 1);
    if (!bgTaskQueue.some((e) => e.serviceId === svc)) {
        recordsRefreshTrigger[svc] = (recordsRefreshTrigger[svc] || 0) + 1;
    }
};
// Hand a fallback-polled result back to the chat engine so the "Indexing:" bubble
// actually resolves — but ONLY while the agent view is mounted. The engine's host is
// a Proxy that THROWS when no view is mounted (ai_agent.ts), and this poller runs
// precisely when the view is closed, so the guard is load-bearing rather than
// defensive. When the view is closed there is no bubble to resolve anyway; the
// history fetch on next mount renders the finished item.
const handOffToChat = (id: string, response: any, platform: string, executedAt?: number) => {
    if (!agentViewMounted.value) return;
    try {
        chatSession.handleHistoryItemResolution(id, response, platform as any, executedAt);
    } catch (err) {
        console.error("[main] could not hand bg result to chat", err);
    }
};
watch(
    // Keyed on the length plus only the ids that can hold a poll, NOT on every id
    // in the queue. A bulk upload pushes one entry per file, and joining all of
    // them rebuilt an n-element string on every push AND every removal, i.e. O(n²)
    // over the batch, on the very page that is rendering during the upload.
    // Polls are always attached to the lowest-indexed unpolled entries (the loop
    // below runs front to back), so a change that can free or fill a slot always
    // shows up in the length or in this window.
    () => (agentViewMounted.value
        ? ""
        : bgTaskQueue.length + ":" + bgTaskQueue.slice(0, MAX_CONCURRENT_BG_POLLS).map((e) => e.id).join(",")),
    () => {
        if (agentViewMounted.value) return;
        // Respect a pause. This poller exists to keep polling while the chat view is
        // closed, which is EXACTLY the state the user asked us to stop polling in — so
        // without this check, pausing on unmount just handed the work to this watcher and
        // nothing actually stopped.
        if (chatSession.isPollingPaused()) return;
        // Nobody is looking at the tab either.
        if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
        for (const entry of bgTaskQueue) {
            // Ration polls exactly as the engine does (see MAX_CONCURRENT_BG_POLLS).
            // Uploading 10,000 files from the db-files page pushed 10,000 entries here
            // and this loop gave every one its own 3-second setInterval, which is what
            // froze the tab. The queue is in push order and the server settles it FIFO,
            // so breaking out of the front of the loop rations the polls onto the only
            // entries that can resolve next; finishBgFallbackTask frees a slot and this
            // watcher re-fires for the next one.
            if (bgFallbackPolls.size >= MAX_CONCURRENT_BG_POLLS) break;
            // Check the ENGINE's registry, not just the local set. skapi's poll() is an
            // uncancellable setInterval, so a second poll on the same id can never be
            // undone: it produces duplicate replies and stranded "Thinking..." bubbles.
            if (bgFallbackPolls.has(entry.id) || chatSession.historyItemPolls.has(entry.id)) continue;
            if (!entry.poll) continue;
            bgFallbackPolls.add(entry.id);
            // Claim the id in the engine's registry as well, so drainBgTaskQueue does
            // not attach a SECOND poll when the user navigates back to the agent view.
            const id = entry.id;
            const svc = entry.serviceId;
            const plat = entry.platform;
            // When the worker BEGAN executing this pass. It rides on the poll's
            // RUNNING ticks only: the terminal read hands back the destination's own
            // answer, which carries nothing of ours. The SDK remembers the last one it
            // saw and gives it to onResponse as a second argument at settle.
            //
            // This poller MUST take it. It is not a redundant third wheel: it claims the
            // id in chatSession.historyItemPolls just below, and both engine pollers skip
            // an id already in that registry, so once this one attaches it is the ONLY
            // poll that pass will ever get. Uploading from the FILES page with the chat
            // closed lands here every time, which is precisely the case the duration was
            // built for, and dropping the second argument here left it with no execution
            // start at all.
            let executedAt: number | undefined;
            const pollPromise: any = entry.poll({
                latency: POLL_INTERVAL,
                onResponse: (_res: any, meta?: { executed?: number }) => {
                    if (meta && typeof meta.executed === "number" && meta.executed > 0) executedAt = meta.executed;
                },
            });
            // Register a REAL handle (not a bare `true`) so pausePolling can stop this
            // poll. A handle with no stop is deliberately left running by the engine, so
            // registering `true` here made these polls unstoppable.
            chatSession.historyItemPolls.set(id, {
                kind: "bg",
                stop: typeof pollPromise?.stop === "function"
                    ? pollPromise.stop.bind(pollPromise)
                    : undefined,
            });
            pollPromise
                .then((response: any) => {
                    // A stopped poll is not a result: leave the queue entry in place so
                    // the chat view can re-attach to it later.
                    if (response && response.status === "stopped") {
                        bgFallbackPolls.delete(id);
                        return;
                    }
                    handOffToChat(id, response, plat, executedAt);
                    finishBgFallbackTask(id, svc);
                })
                .catch(() => finishBgFallbackTask(id, svc));
        }
    },
    { immediate: true }
);

// ─── waiting for a project a paid checkout is creating ───
// Every 3 s for the first 2 minutes, then every 10 s; after 15 minutes stop
// and say so (the project still lands in the list whenever it is created).
const AWAIT_FAST_MS = 3000;
const AWAIT_SLOW_MS = 10000;
const AWAIT_SLOW_AFTER_MS = 2 * 60 * 1000;
const AWAIT_GIVE_UP_MS = 15 * 60 * 1000;
const awaiting = ref<"" | "waiting" | "slow" | "gave-up">("");
let awaitTimer: ReturnType<typeof setTimeout> | null = null;
let awaitStopped = false;

async function awaitNewProject(id: string) {
    awaiting.value = "waiting";
    removeFullScreenLoader();
    const started = Date.now();
    const poll = async () => {
        if (awaitStopped) return;
        let found: any = null;
        try {
            found = await pullServices({ service_id_to_fetch: id });
            // a list entry can still be loading
            if (found instanceof Promise) found = await found;
        } catch (err) {
            console.warn("[main] could not check for the new project", err);
        }
        if (awaitStopped) return;
        if (found) {
            // The checkout carried the base prompt cut to what it can hold; a
            // longer one is written whole now that the project exists.
            const complete = await completeNewProject(id, found);
            if (awaitStopped) return;
            if (!complete) {
                showToast(
                    `Your project was created with the first ${CHECKOUT_DESCRIPTION_MAX} characters of its base prompt. You can complete it in Settings.`,
                    "warning",
                );
            }
            awaiting.value = "";
            serviceId = id;
            currentService.value = found;
            // The new path raises the router's full-screen overlay, and on a project
            // route the router leaves removing it to this component, whose run() does
            // not run again for the same component instance: drop it here.
            await router.replace(newProjectPath(id));
            removeFullScreenLoader();
            warmProject(found);
            return;
        }
        const elapsed = Date.now() - started;
        if (elapsed >= AWAIT_GIVE_UP_MS) {
            awaiting.value = "gave-up";
            return;
        }
        if (elapsed >= AWAIT_SLOW_AFTER_MS) awaiting.value = "slow";
        awaitTimer = setTimeout(poll, elapsed >= AWAIT_SLOW_AFTER_MS ? AWAIT_SLOW_MS : AWAIT_FAST_MS);
    };
    await poll();
}

onUnmounted(() => {
    awaitStopped = true;
    if (awaitTimer) clearTimeout(awaitTimer);
});

/** The fetches a project page starts the moment its project is known. */
function warmProject(service: any) {
    // Kick off the AI API-key fetch FIRST, the moment the service is known, so
    // it's the earliest request on project open (ahead of subdomain info) and is
    // already warm by the time the agent view mounts. Deduped + non-blocking;
    // refreshGate reuses this in-flight request instead of firing its own.
    void prefetchAiApiKey(service);
    // Warm the tab caches too (fire-and-forget, deduped, publish-only-if-empty):
    // Records/Users then paint instantly from their module pagers, and dbfile's
    // dir watcher starts its listing without waiting its own getDirInfo.
    void prefetchServiceRecords(service);
    void prefetchServiceUsers(service);
    prefetchDbDirInfo(service);
}

async function run() {
    currentService.value = null;
    // Plan & Usage draws the served limits; a page opened straight on a project
    // has no plan cards to fetch them, so ask here (a no-op once they are in).
    void loadPlanSpecs();

    if (serviceId.startsWith("await-")) {
        return awaitNewProject(serviceId.replace("await-", ""));
    }
    fullScreenLoader();

    let serviceInst = serviceList?.[serviceId];
    if (!serviceInst) {
        serviceInst = pullServices({ service_id_to_fetch: serviceId });
    }

    if (serviceInst instanceof Promise) {
        serviceInst = await serviceInst;
    }

    if (!serviceInst) {
        removeFullScreenLoader();
        router.push('/my-projects');
        return;
    }

    currentService.value = serviceInst;
    removeFullScreenLoader();
    warmProject(currentService.value);

    // // Subdomain info (only the service views need it) — on VISIT, but only ONCE
    // // per service: subdInfo.srvc is set the first time a fetch settles, and the
    // // Service instance is cached across visits, so skip the re-fetch on re-entry.
    // // (The hosting view still re-fetches via getInfo() after an actual subdomain
    // // change; the internal poll's own timer guard covers in-progress fetches.)
    // if (!currentService.value?.subdInfo?.srvc) {
    //     currentService.value?.getSubdomainInfo?.();
    // }
}
run();

// The provider's customer portal; see openBillingPortal in code/user.ts.
let openBillingPage = () => openBillingPortal();
</script>

<style lang="less" scoped>
@import "@/assets/less/bq-tokens.less";

.bq-await {
    max-width: 36rem;
    margin: 0 auto;
    padding: 5rem 1.5rem;
    text-align: center;
    font-family: @mono;
    color: @ink;
}

.bq-await-title {
    font-size: clamp(1.2rem, 3vw, 1.6rem);
    font-weight: 700;
    margin: 1.5rem 0 0.75rem;
}

.bq-await-desc {
    color: @muted;
    font-size: 0.88rem;
    line-height: 1.6;
    margin: 0 0 1.5rem;
}

.bq-shell-loading {
    min-height: 50vh;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: @mono;
    color: @ink;
}

.bq-service-layout {
    display: flex;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    background: @paper;
    color: @ink;
    font-family: @mono;
}

.bq-sidebar {
    position: sticky;
    top: calc(4rem + var(--nav-top));
    width: 200px;
    flex-shrink: 0;
    overflow-y: auto;
    align-self: flex-start;
}

.bq-main {
    flex: 1;
    padding: 3rem 3rem 3rem;
    min-width: 0;
}

/* The banners here are the shared .bq-banner from bq-shared.less: same layout,
   type and icon size as the news banner on the project list. */
.bq-link {
    &:hover {
        text-decoration: underline;
    }

    // Waiting on its action (see runAction): no second click, no hover promise.
    &.is-busy {
        pointer-events: none;
        opacity: 0.7;
    }
}

@media (max-width: 800px) {
    .bq-sidebar {
        display: none;
    }

    .bq-main {
        padding: 1rem;
    }
}
</style>
